/**
 * StockLocationFormDialog - Cadastro/edição de endereço de estoque
 *
 * Campo Tipo (Depósito, Loja, Escritório, Casa do Cliente) e endereço
 * preenchido pelo CEP (ViaCEP, mesmo serviço já usado em Clientes).
 * A validação definitiva acontece na RPC `upsert_stock_location`.
 *
 * RELEASE: 2026-10-08 v1.2.0 - Opção "Endereço padrão" (BR-EST-024/025); o padrão não pode ser desmarcado aqui.
 * RELEASE: 2026-10-07 v1.1.0 - Padroniza o modal conforme o cadastro de Pessoas:
 *          cabeçalho e rodapé fixos, seções, responsividade e busca explícita de CEP.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 2 do Estoque).
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin, Search, StickyNote, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FormSection, FieldLabel } from '@/components/common/FormSection';
import { useIsMobile } from '@/hooks/use-mobile';
import { applyCEPMask } from '@/lib/input-masks';
import { fetchAddressByCep } from '@/lib/cep-lookup';
import { cn } from '@/lib/utils';
import { STOCK_LOCATION_TYPES, useStockRegistryMutations, type StockLocation, type StockLocationType } from '@/hooks/useStockRegistries';

type Form = Omit<StockLocation, 'id' | 'is_active'> & { cep: string };
const EMPTY: Form = {
  name: '', location_type: 'depot', cep: '', street: '', address_number: '', complement: '',
  district: '', city: '', state: '', notes: '', is_default: false,
};

interface Props {
  open: boolean;
  location: StockLocation | null;
  onOpenChange: (open: boolean) => void;
}

export function StockLocationFormDialog({ open, location, onOpenChange }: Props) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState<Form>(EMPTY);
  const [searchingCep, setSearchingCep] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const addressNumberRef = useRef<HTMLInputElement | null>(null);
  const { upsertLocation } = useStockRegistryMutations();

  // Carrega o registro ao abrir (ou limpa para um novo)
  useEffect(() => {
    if (!open) return;
    setNameError(null);
    setForm(location ? {
      name: location.name, location_type: location.location_type, cep: applyCEPMask(location.cep ?? ''), street: location.street ?? '',
      address_number: location.address_number ?? '', complement: location.complement ?? '', district: location.district ?? '',
      city: location.city ?? '', state: location.state ?? '', notes: location.notes ?? '',
      is_default: !!location.is_default,
    } : EMPTY);
  }, [open, location]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  /** Preenche o endereço pelo CEP (8 dígitos). */
  const lookupCep = async () => {
    setSearchingCep(true);
    try {
      const address = await fetchAddressByCep(form.cep);
      setForm((f) => ({
        ...f,
        street: address.street,
        district: address.district,
        city: address.city,
        state: address.state,
      }));
      addressNumberRef.current?.focus();
      toast.success('Endereço preenchido!');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSearchingCep(false);
    }
  };

  const save = () => {
    if (!form.name.trim()) {
      setNameError('Nome do endereço é obrigatório');
      return;
    }
    setNameError(null);
    upsertLocation.mutate(
      { id: location?.id ?? null, data: { ...form, is_active: location?.is_active ?? true } },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const inputCls = 'h-9 border-border/50';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex flex-col gap-0 overflow-hidden p-0',
          isMobile ? 'h-[100dvh] w-screen max-w-full rounded-none' : 'max-h-[90vh] max-w-5xl',
        )}
      >
        {/* Cabeçalho fixo no mesmo padrão dos cadastros de Pessoas. */}
        <DialogHeader className="shrink-0 border-b p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5">
              {upsertLocation.isPending
                ? <Loader2 className="h-5 w-5 animate-spin text-primary" />
                : <Warehouse className="h-5 w-5 text-primary" />}
            </div>
            <div className="text-left">
              <DialogTitle className="text-lg">{location ? 'Editar endereço de estoque' : 'Novo endereço de estoque'}</DialogTitle>
              <DialogDescription className="text-sm">
                {location ? 'Atualize as informações do endereço abaixo.' : 'Preencha os dados do novo endereço de estoque.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
            {/* Identificação do endereço e classificação operacional. */}
            <FormSection icon={Warehouse} title="Identificação">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel required>Nome</FieldLabel>
                  <Input
                    className={inputCls}
                    value={form.name}
                    maxLength={120}
                    onChange={(e) => {
                      set('name', e.target.value);
                      if (nameError) setNameError(null);
                    }}
                    placeholder="Ex.: Almoxarifado Central"
                    aria-invalid={!!nameError}
                  />
                  {nameError && <p className="text-[0.8rem] font-medium text-destructive">{nameError}</p>}
                </div>
                <div className="space-y-1.5">
                  <FieldLabel required>Tipo</FieldLabel>
                  <Select value={form.location_type} onValueChange={(v) => set('location_type', v as StockLocationType)}>
                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STOCK_LOCATION_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </FormSection>

            <Separator />

            {/* Endereço com a mesma busca explícita de CEP do cadastro de Pessoas. */}
            <FormSection icon={MapPin} title="Endereço" hint="(opcional)">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel>CEP</FieldLabel>
                  <div className="flex gap-2">
                    <Input
                      className={inputCls}
                      value={form.cep}
                      inputMode="numeric"
                      onChange={(e) => set('cep', applyCEPMask(e.target.value))}
                      placeholder="00000-000"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 shrink-0"
                      onClick={lookupCep}
                      disabled={searchingCep}
                      aria-label="Buscar CEP"
                    >
                      {searchingCep ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5 sm:col-span-3">
                  <FieldLabel>Logradouro</FieldLabel>
                  <Input className={inputCls} value={form.street ?? ''} maxLength={150} onChange={(e) => set('street', e.target.value)} placeholder="Rua, avenida..." />
                </div>
                <div className="space-y-1.5 sm:col-span-1">
                  <FieldLabel>Número</FieldLabel>
                  <Input ref={addressNumberRef} className={inputCls} value={form.address_number ?? ''} maxLength={20} onChange={(e) => set('address_number', e.target.value)} placeholder="Nº" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel>Complemento</FieldLabel>
                  <Input className={inputCls} value={form.complement ?? ''} maxLength={100} onChange={(e) => set('complement', e.target.value)} placeholder="Apto, sala..." />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel>Bairro</FieldLabel>
                  <Input className={inputCls} value={form.district ?? ''} maxLength={100} onChange={(e) => set('district', e.target.value)} placeholder="Bairro" />
                </div>
                <div className="space-y-1.5 sm:col-span-1">
                  <FieldLabel>Cidade</FieldLabel>
                  <Input className={inputCls} value={form.city ?? ''} maxLength={100} onChange={(e) => set('city', e.target.value)} placeholder="Cidade" />
                </div>
                <div className="space-y-1.5 sm:col-span-1">
                  <FieldLabel>UF</FieldLabel>
                  <Input className={inputCls} value={form.state ?? ''} maxLength={2} onChange={(e) => set('state', e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())} placeholder="UF" />
                </div>
              </div>
            </FormSection>

            <Separator />

            {/* Endereço padrão: destino automático da montagem de kit (BR-EST-026) */}
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border/50 p-4">
              <div>
                <p className="text-sm font-medium">Endereço padrão</p>
                <p className="text-xs text-muted-foreground">
                  {location?.is_default
                    ? 'Este é o endereço padrão. Para trocar, marque outro endereço como padrão.'
                    : 'Kits montados vão para este endereço. Ao marcar, o padrão anterior é substituído.'}
                </p>
              </div>
              <Switch checked={form.is_default} disabled={!!location?.is_default || (location ? !location.is_active : false)}
                onCheckedChange={(v) => set('is_default', v)} aria-label="Endereço padrão" />
            </div>

            <Separator />

            <FormSection icon={StickyNote} title="Observações" hint="(opcional)">
              <Textarea
                className="resize-none border-border/50"
                value={form.notes ?? ''}
                maxLength={1000}
                rows={3}
                onChange={(e) => set('notes', e.target.value)}
                placeholder="Observações sobre o endereço..."
              />
            </FormSection>
          </div>
        </ScrollArea>

        {/* Rodapé fixo preserva as ações durante a rolagem. */}
        <DialogFooter className="shrink-0 gap-2 border-t p-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="button" onClick={save} disabled={upsertLocation.isPending}>
            {upsertLocation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {location ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
